import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CmsCardComponent } from '../cms-card/cms-card';
import { CmsApiService, CmsItem } from '../cms-api/cms-api.service';
import { NEWS_LABELS } from '@arrk-engineering-gmbh/shared';
const ALL_VALUE = '__all__';
type CardNews = CmsItem & { _cardId: number };
@Component({
  selector: 'lib-cms-news',
  standalone: true,
  imports: [CmsCardComponent, Button, SelectButton, FormsModule, CdkDropList, CdkDrag, Toast],
  providers: [MessageService],
  templateUrl: './cms-news.html',
  styleUrl: './cms-news.scss'
})
export class CmsNewsComponent implements OnInit {
  private readonly api = inject(CmsApiService);
  private readonly messageService = inject(MessageService);
  private nextId = 0;
  readonly news = signal<CardNews[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly newsLabels = signal<string[]>([...NEWS_LABELS]);
  readonly labelOptions = computed<{ label: string; value: string }[]>(() => [
    { label: 'All', value: ALL_VALUE },
    ...this.newsLabels().map((l: string) => ({ label: l, value: l }))
  ]);
  readonly selectedLabels = signal<string[]>([]);
  readonly displayedSelection = computed<string[]>(() =>
    this.selectedLabels().length === 0 ? [ALL_VALUE] : this.selectedLabels()
  );
  readonly filteredNews = computed<CardNews[]>(() => {
    const selected = this.selectedLabels();
    if (!selected.length) return this.news();
    return this.news().filter((n: CardNews) => n.labels.some((l: string) => selected.includes(l)));
  });
  readonly hasInvalidCards = computed<boolean>(() =>
    this.news().some((n: CardNews) => !n.title.trim() || !n.description.trim() || !n.image || n.labels.length === 0)
  );

  ngOnInit(): void {
    this.api.getNews().subscribe({
      next: (items: CmsItem[]) => {
        this.news.set(items.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.loading.set(false);
      },
      error: (err: unknown) => {
        console.error('[CmsNews] Failed to load', err);
        this.error.set('Failed to load news from server.');
        this.loading.set(false);
      }
    });
  }

  onAddNews(): void {
    this.news.update((list: CardNews[]) => [
      { title: '', description: '', image: '', labels: [], _cardId: this.nextId++ },
      ...list
    ]);
  }

  onNewsChange(cardId: number, updated: CmsItem): void {
    this.news.update((list: CardNews[]) =>
      list.map((item: CardNews) => item._cardId === cardId ? { ...updated, _cardId: item._cardId } : item)
    );
  }

  onDeleteNews(cardId: number): void {
    const item = this.news().find((n: CardNews) => n._cardId === cardId);
    if (item?.id != null) {
      this.api.deleteNews(item.id).subscribe({
        error: (err: unknown) => console.error('[CmsNews] Delete failed', err)
      });
    }
    this.news.update((list: CardNews[]) => list.filter((n: CardNews) => n._cardId !== cardId));
  }

  onDrop(event: CdkDragDrop<CardNews[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const visibleIds = this.filteredNews().map((item: CardNews) => item._cardId);
    this.news.update((list: CardNews[]) => {
      if (visibleIds.length === list.length) {
        const next = [...list];
        moveItemInArray(next, event.previousIndex, event.currentIndex);
        return next;
      }
      const reordered = list.filter((item: CardNews) => visibleIds.includes(item._cardId));
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      const queue = [...reordered];
      return list.map((item: CardNews) => visibleIds.includes(item._cardId) ? (queue.shift() ?? item) : item);
    });
  }

  onLabelFilterChange(values: string[] | string | null | undefined): void {
    const vals: string[] = Array.isArray(values) ? values : values ? [values] : [];
    if (!vals.length) { this.selectedLabels.set([]); return; }
    if (vals.includes(ALL_VALUE) && this.selectedLabels().length > 0) { this.selectedLabels.set([]); return; }
    const real = vals.filter((v: string) => v !== ALL_VALUE);
    this.selectedLabels.set(real.length ? real : []);
  }

  onPublish(): void {
    if (this.hasInvalidCards()) return;
    this.saving.set(true);
    const payload = this.news().map(({ _cardId, ...rest }: CardNews) => rest);
    this.api.publishNews(payload).subscribe({
      next: (saved: CmsItem[]) => {
        this.news.set(saved.map((item: CmsItem) => ({ ...item, _cardId: this.nextId++ })));
        this.saving.set(false);
        this.messageService.add({ severity: 'success', summary: 'Published', detail: 'News published successfully.' });
      },
      error: (err: unknown) => {
        console.error('[CmsNews] Publish failed', err);
        this.saving.set(false);
        this.messageService.add({ severity: 'error', summary: 'Publish failed', detail: 'Could not publish news. Please try again.' });
      }
    });
  }
}
